import React from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaCheck, FaTimes, FaArrowRight } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import SEO from '../components/SEO';

const Comparison: React.FC = () => {
  const casinos = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: `${process.env.PUBLIC_URL}/images/offers/allbritishcasino.webp`,
      rating: 4.8,
      bonus: '100% up to £100',
      wagering: '35x',
      minDeposit: '£10',
      payoutTime: '0-24h',
      games: 2000,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      withdrawalSpeed: 98,
    },
    {
      id: 'casinocasino',
      name: 'Casino Casino',
      image: `${process.env.PUBLIC_URL}/images/offers/casinocasino.webp`,
      rating: 4.7,
      bonus: '200% up to £200',
      wagering: '40x',
      minDeposit: '£20',
      payoutTime: '1-48h',
      games: 2500,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      withdrawalSpeed: 95,
    },
    {
      id: 'pubcasino',
      name: 'Pub Casino',
      image: `${process.env.PUBLIC_URL}/images/offers/pubcasino.webp`,
      rating: 4.6,
      bonus: '150% up to £150',
      wagering: '30x',
      minDeposit: '£10',
      payoutTime: '0-48h',
      games: 1800,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      withdrawalSpeed: 92,
    },
  ];

  const comparisonData = casinos.map(casino => ({
    name: casino.name.split(' ')[0],
    rating: casino.rating * 20,
    withdrawalSpeed: casino.withdrawalSpeed,
    games: casino.games / 25,
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: casinos.map((casino, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Organization',
        name: casino.name,
        rating: {
          '@type': 'Rating',
          ratingValue: casino.rating,
          bestRating: 5,
        },
      },
    })),
  };

  return (
    <>
      <SEO
        title="Compare UK Casino Offers - Side-by-Side Comparison"
        description="Compare top UK casinos side-by-side. Compare bonuses, wagering requirements, withdrawal speeds, game selections, and more to find your perfect match."
        canonical="/comparison"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-dark-900">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">Compare</span> <span className="text-white">Casino Offers</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Side-by-side comparison of top UK casinos. Find the best bonuses, fastest payouts, 
                and most generous terms all in one place.
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Chart */}
        <section className="py-12 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="glass rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6 text-center">
                <span className="text-gradient">Performance Comparison</span>
              </h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="rating" fill="#0ea5e9" name="Rating Score" />
                  <Bar dataKey="withdrawalSpeed" fill="#3b82f6" name="Withdrawal Speed %" />
                  <Bar dataKey="games" fill="#60a5fa" name="Game Variety Score" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-dark-900">
          <div className="container mx-auto px-4">
            <div className="overflow-x-auto">
              <div className="glass rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-dark-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Casino</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Rating</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Welcome Bonus</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Wagering</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Min Deposit</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Payout Time</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Games</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Mobile</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {casinos.map((casino, index) => (
                      <tr
                        key={casino.id}
                        className={`border-t border-dark-700 ${index % 2 === 0 ? 'bg-dark-800/50' : 'bg-dark-900'}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-20 h-20 rounded-lg p-2 flex items-center justify-center flex-shrink-0 border-2 border-gray-400 shadow-lg">
                              <img
                                src={casino.image}
                                alt={casino.name}
                                className="max-w-[64px] max-h-[64px] w-auto h-auto object-contain"
                                onError={(e) => {
                                  console.error('Image failed to load:', casino.image);
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<div class="text-xs text-gray-600 text-center font-semibold">Logo</div>';
                                  }
                                }}
                                onLoad={(e) => {
                                  console.log('Image loaded:', casino.image, e.target);
                                }}
                              />
                            </div>
                            <span className="text-white font-semibold">{casino.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <FaStar className="w-4 h-4 text-yellow-400" />
                            <span className="text-white font-semibold">{casino.rating}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-quantum-400 font-semibold">{casino.bonus}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-gray-300">{casino.wagering}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-gray-300">{casino.minDeposit}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-gray-300">{casino.payoutTime}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-gray-300">{casino.games.toLocaleString()}+</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {casino.mobile ? (
                            <FaCheck className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <FaTimes className="w-5 h-5 text-red-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Link
                            to={`/review/${casino.id}`}
                            className="inline-flex items-center space-x-2 text-quantum-400 hover:text-quantum-300 transition-colors"
                          >
                            <span>Review</span>
                            <FaArrowRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              <span className="text-gradient">Feature Comparison</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {casinos.map((casino) => (
                <div key={casino.id} className="glass rounded-xl p-6">
                  <div className="mb-6">
                    <div className="w-full h-32 rounded-lg mb-4 p-4 flex items-center justify-center border border-gray-300">
                      <img
                        src={casino.image}
                        alt={casino.name}
                        className="max-w-full max-h-full object-contain"
                        style={{ minHeight: '60px', minWidth: '60px' }}
                        onError={(e) => {
                          console.error('Image failed to load:', casino.image);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="text-sm text-gray-500 text-center">Image not available</div>';
                          }
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', casino.image);
                        }}
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{casino.name}</h3>
                    <div className="flex items-center space-x-1 mb-4">
                      <FaStar className="w-5 h-5 text-yellow-400" />
                      <span className="text-white font-semibold">{casino.rating}</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {[
                      { label: 'UKGC Licensed', value: true },
                      { label: 'Mobile Optimized', value: casino.mobile },
                      { label: 'Live Chat Support', value: casino.liveChat },
                      { label: 'Fast Withdrawals', value: casino.withdrawalSpeed > 90 },
                      { label: 'Large Game Library', value: casino.games > 1500 },
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span className="text-gray-300">{feature.label}</span>
                        {feature.value ? (
                          <FaCheck className="w-5 h-5 text-green-400" />
                        ) : (
                          <FaTimes className="w-5 h-5 text-red-400" />
                        )}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/review/${casino.id}`}
                    className="block w-full bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-3 px-6 rounded-lg font-semibold text-center hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105"
                  >
                    Read Full Review
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-quantum-900/20 to-dark-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Found Your Perfect Match?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Click through to read our detailed reviews and claim exclusive welcome bonuses. 
              New players only. 18+ | BeGambleAware.org
            </p>
            <Link
              to="/"
              className="inline-block bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105 shadow-lg shadow-quantum-500/30"
            >
              View All Offers
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Comparison;

